import { useState, useRef, useEffect } from "react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { roleLabel, isExternal } from "@/lib/roles";
import { RefreshCcw, ChevronDown, Check } from "lucide-react";

/** Session-level role switcher for external accounts with 2+ approved roles.
 *  Server validates the switch. Internal role changes are NOT switchable here — those
 *  require Super Administrator staff assignment. */
export default function RoleSwitcher() {
  const { user, switchRole } = useAuth();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const on = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", on);
    return () => document.removeEventListener("mousedown", on);
  }, []);

  if (!user || !user.roles) return null;
  const approved = (user.roles || []).filter((r) => ["buyer", "seller", "dealer", "inspector", "service_partner"].includes(r));
  if (approved.length < 2) return null;
  if (!isExternal(user)) return null;

  const doSwitch = async (role) => {
    if (role === user.role) { setOpen(false); return; }
    const r = await switchRole(role);
    if (r.ok) {
      toast.success(`Switched to ${roleLabel(role)}.`);
      setOpen(false);
      // The dashboard/nav update automatically via user context.
    } else {
      toast.error(r.error || "Role switch failed.");
    }
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 border border-ivory-400 bg-ivory px-3 py-1.5 text-[11px] uppercase tracking-[0.2em] text-ink-50 hover:text-copper hover:border-copper transition"
        data-testid="role-switcher-btn"
      >
        <RefreshCcw className="w-3.5 h-3.5" />
        {roleLabel(user.role)}
        <ChevronDown className="w-3 h-3" />
      </button>
      {open && (
        <div className="absolute top-full right-0 mt-2 min-w-[220px] bg-ivory border border-ivory-400 shadow-lg z-50" data-testid="role-switcher-menu">
          <div className="px-3 py-2 text-[10px] uppercase tracking-[0.22em] text-ink-100 border-b border-ivory-400">
            Active role
          </div>
          <ul>
            {approved.map((r) => (
              <li key={r}>
                <button
                  onClick={() => doSwitch(r)}
                  className={`w-full text-left px-3 py-2 text-sm flex items-center justify-between hover:bg-ivory-100 ${r === user.role ? "text-copper" : "text-ink"}`}
                  data-testid={`role-switch-option-${r}`}
                >
                  <span>{roleLabel(r)}</span>
                  {r === user.role && <Check className="w-3.5 h-3.5 text-copper" />}
                </button>
              </li>
            ))}
          </ul>
          <div className="px-3 py-2 text-[10px] text-ink-100 border-t border-ivory-400 leading-relaxed">
            Only roles approved on your account are listed. Internal staff roles are not switchable here.
          </div>
        </div>
      )}
    </div>
  );
}
